import {
  EXPERT_TYPES,
  PARTNER_TIERS,
  queryParamsFromInput,
} from './superCollectionPreset';

/**
 * Structured editing model for the `experts-smart-filter` field type. The
 * persisted value stays the normalized /experts/all query string that
 * `normalizeSuperCollectionPreset` accepts, so pages saved through the popup
 * remain readable by the website's `parsePresetFiltersQuery` and by older
 * textarea-based schemas.
 */
export interface ExpertsSmartFilterState {
  expert_type: string;
  services_offered: string[];
  countries: string[];
  cities: string[];
  languages: string[];
  apps_and_themes: string[];
  partner_tiers: string[];
  partner_badge: string;
  price_from: string;
  price_to: string;
  search: string;
  is_pro: boolean;
  ranking: string;
}

export const LIST_STATE_KEYS = [
  'services_offered',
  'countries',
  'cities',
  'languages',
  'apps_and_themes',
  'partner_tiers',
] as const;

export type ListStateKey = typeof LIST_STATE_KEYS[number];

export function createEmptySmartFilterState(): ExpertsSmartFilterState {
  return {
    expert_type: '',
    services_offered: [],
    countries: [],
    cities: [],
    languages: [],
    apps_and_themes: [],
    partner_tiers: [],
    partner_badge: '',
    price_from: '',
    price_to: '',
    search: '',
    is_pro: false,
    ranking: '',
  };
}

function listFromParam(raw: string | null): string[] {
  if (!raw) return [];
  return [...new Set(raw.split(',').map(value => value.trim()).filter(Boolean))];
}

function digitsOrEmpty(raw: string | null): string {
  const value = raw?.trim() || '';
  return /^\d+$/.test(value) ? value : '';
}

/**
 * Parse a stored preset value (query string or pasted /experts/all URL) into
 * the structured state. Unknown keys and malformed values are dropped — the
 * popup edits only what it can represent, and serialization rebuilds the
 * value from this state.
 */
export function parseExpertsSmartFilterValue(input: unknown): ExpertsSmartFilterState {
  const state = createEmptySmartFilterState();
  if (typeof input !== 'string' || !input.trim()) return state;

  const { params } = queryParamsFromInput(input);

  const expertType = params.get('expert_type')?.trim() || '';
  if (EXPERT_TYPES.has(expertType)) state.expert_type = expertType;

  for (const key of LIST_STATE_KEYS) {
    const entries = listFromParam(params.get(key));
    if (key === 'partner_tiers') state[key] = entries.filter(value => PARTNER_TIERS.has(value));
    else if (key === 'cities') state[key] = entries.filter(value => /^\d+$/.test(value) && Number.isSafeInteger(Number(value)));
    else state[key] = entries;
  }

  state.partner_badge = params.get('partner_badge')?.trim() || '';
  state.price_from = digitsOrEmpty(params.get('price_from'));
  state.price_to = digitsOrEmpty(params.get('price_to'));
  state.search = params.get('search')?.trim() || '';
  state.is_pro = params.get('is_pro')?.trim() === 'true';
  state.ranking = params.get('ranking')?.trim() === 'most_recent' ? 'most_recent' : '';

  return state;
}

/**
 * Serialize the structured state back to the canonical query string, in the
 * same key order `normalizeSuperCollectionPreset` emits.
 */
export function serializeExpertsSmartFilterValue(state: ExpertsSmartFilterState): string {
  const params = new URLSearchParams();

  if (state.expert_type && EXPERT_TYPES.has(state.expert_type)) {
    params.set('expert_type', state.expert_type);
  }
  for (const key of LIST_STATE_KEYS) {
    const entries = [...new Set(state[key].map(value => value.trim()).filter(Boolean))];
    if (entries.length) params.set(key, entries.join(','));
  }
  if (state.partner_badge.trim()) params.set('partner_badge', state.partner_badge.trim());
  if (/^\d+$/.test(state.price_from)) params.set('price_from', String(Number(state.price_from)));
  if (/^\d+$/.test(state.price_to)) params.set('price_to', String(Number(state.price_to)));
  if (state.search.trim()) params.set('search', state.search.trim());
  if (state.is_pro) params.set('is_pro', 'true');
  if (state.ranking === 'most_recent') params.set('ranking', 'most_recent');

  return params.toString();
}

export function isSmartFilterStateEmpty(state: ExpertsSmartFilterState): boolean {
  return !serializeExpertsSmartFilterValue(state);
}

export function countActiveSmartFilters(state: ExpertsSmartFilterState): number {
  let count = 0;
  if (state.expert_type) count += 1;
  for (const key of LIST_STATE_KEYS) count += state[key].length;
  if (state.partner_badge.trim()) count += 1;
  if (state.price_from || state.price_to) count += 1;
  if (state.search.trim()) count += 1;
  if (state.is_pro) count += 1;
  if (state.ranking) count += 1;
  return count;
}
